/**
 * Meta Connection Guard
 * 
 * Wrapper component that ensures Meta account is connected before showing analytics content
 */

import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import * as Icons from 'lucide-react'
import { useMetaConnection } from '../contexts/MetaConnectionContext'
import { ROUTES } from '../config'

interface MetaConnectionGuardProps {
  children: ReactNode
  title?: string
  description?: string
  icon?: keyof typeof Icons
}

export function MetaConnectionGuard({
  children,
  title = 'Connect Meta Account',
  description = 'Connect your Meta Ads account to view analytics data.',
  icon = 'BarChart3',
}: MetaConnectionGuardProps) {
  const { connection } = useMetaConnection()

  // Loading state
  if (connection.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Icons.Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-tf-text-muted">Checking connection...</p>
        </div>
      </div>
    )
  }

  // Not connected state - redirect to config page
  if (!connection.connected) {
    const IconComponent = (Icons as unknown as Record<string, typeof Icons.BarChart3>)[icon] || Icons.BarChart3

    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-tf-bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <IconComponent className="w-10 h-10 text-tf-text-muted" />
          </div>
          
          <h2 className="text-2xl font-bold text-tf-text mb-3">{title}</h2>
          <p className="text-tf-text-muted mb-8">{description}</p>

          <div className="bg-tf-bg-card rounded-xl border border-tf-border p-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-[#1877f2] flex items-center justify-center flex-shrink-0">
                <Icons.Facebook className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-base font-semibold text-tf-text">Meta Ads Integration</h3>
                <p className="text-sm text-tf-text-muted">Connect once, use everywhere</p>
              </div>
            </div>

            <Link
              to={ROUTES.SETTINGS.META_CONFIG}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
            >
              <Icons.Settings className="w-4 h-4" />
              Go to Configuration
            </Link>

            <p className="text-xs text-tf-text-muted mt-4">
              Configure your Meta Ads connection in the settings page to unlock analytics features.
            </p>
          </div>

          {/* Features list */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-tf-bg-muted flex items-center justify-center mx-auto mb-2">
                <Icons.TrendingUp className="w-5 h-5 text-tf-text-muted" />
              </div>
              <p className="text-xs font-medium text-tf-text">Analytics</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-tf-bg-muted flex items-center justify-center mx-auto mb-2">
                <Icons.PieChart className="w-5 h-5 text-tf-text-muted" />
              </div>
              <p className="text-xs font-medium text-tf-text">Budgets</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-tf-bg-muted flex items-center justify-center mx-auto mb-2">
                <Icons.Image className="w-5 h-5 text-tf-text-muted" />
              </div>
              <p className="text-xs font-medium text-tf-text">Creatives</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Connected - show content
  return <>{children}</>
}
